import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Space,
    Modal,
    Form,
    Input,
    message,
    Popconfirm,
    Card,
    Row,
    Col,
    Tooltip
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { ApiService } from '../services/api';
import {
    App,
    AppListRequest,
    CreateAppRequest,
    UpdateAppRequest
} from '../types';

const AppManagement: React.FC = () => {
    const [apps, setApps] = useState<App[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [total, setTotal] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const pageSize = 20;

    // 模态框状态
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [editingApp, setEditingApp] = useState<App | null>(null);
    const [form] = Form.useForm();

    // 获取应用列表
    const fetchApps = async (page: number = currentPage) => {
        setLoading(true);
        try {
            const request: AppListRequest = {
                page,
                page_size: pageSize
            };
            const response = await ApiService.getAppList(request);
            if (response.code === 200) {
                setApps(response.data.apps);
                setTotal(response.data.total);
                setCurrentPage(page);
            } else {
                message.error(response.message || '获取应用列表失败');
            }
        } catch (error) {
            console.error('获取应用列表失败:', error);
            message.error('获取应用列表失败');
        } finally {
            setLoading(false);
        }
    };

    // 页面加载时获取数据
    useEffect(() => {
        fetchApps(1);
    }, []);

    // 处理新增应用
    const handleAdd = () => {
        setEditingApp(null);
        setIsModalVisible(true);
        form.resetFields();
    };

    // 处理编辑应用
    const handleEdit = (app: App) => {
        setEditingApp(app);
        setIsModalVisible(true);
        form.setFieldsValue({
            app_name: app.app_name,
            app_tag: app.app_tag
        });
    };

    // 处理删除应用
    const handleDelete = async (id: number) => {
        try {
            const response = await ApiService.deleteApp({ id });
            if (response.code === 200) {
                message.success(response.message || '应用删除成功');
                fetchApps(currentPage);
            } else {
                message.error(response.message || '应用删除失败');
            }
        } catch (error) {
            console.error('删除应用失败:', error);
            message.error('删除应用失败');
        }
    };

    // 处理模态框确认
    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            if (editingApp) {
                // 编辑应用
                const request: UpdateAppRequest = {
                    id: editingApp.id,
                    app_name: values.app_name,
                    app_tag: values.app_tag
                };
                const response = await ApiService.updateApp(request);
                if (response.code === 200) {
                    message.success(response.message || '应用更新成功');
                    setIsModalVisible(false);
                    fetchApps(currentPage);
                } else {
                    message.error(response.message || '应用更新失败');
                }
            } else {
                // 新增应用
                const request: CreateAppRequest = {
                    app_name: values.app_name,
                    app_tag: values.app_tag
                };
                const response = await ApiService.createApp(request);
                if (response.code === 200) {
                    message.success(response.message || '应用创建成功');
                    setIsModalVisible(false);
                    fetchApps(1); // 新增后回到第一页
                } else {
                    message.error(response.message || '应用创建失败');
                }
            }
        } catch (error) {
            console.error('提交失败:', error);
        }
    };

    // 处理模态框取消
    const handleModalCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
        setEditingApp(null);
    };

    // 表格列定义
    const columns: ColumnsType<App> = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80
        },
        {
            title: '应用名称',
            dataIndex: 'app_name',
            key: 'app_name',
            ellipsis: {
                showTitle: false,
            },
            render: (text: string) => (
                <Tooltip placement="topLeft" title={text}>
                    {text}
                </Tooltip>
            )
        },
        {
            title: '应用标签',
            dataIndex: 'app_tag',
            key: 'app_tag',
            ellipsis: {
                showTitle: false,
            },
            render: (text: string) => (
                <Tooltip placement="topLeft" title={text}>
                    {text}
                </Tooltip>
            )
        },
        {
            title: '创建时间',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 180,
            render: (text: string) => new Date(text).toLocaleString('zh-CN')
        },
        {
            title: '更新时间',
            dataIndex: 'updated_at',
            key: 'updated_at',
            width: 180,
            render: (text: string) => new Date(text).toLocaleString('zh-CN')
        },
        {
            title: '操作',
            key: 'action',
            width: 160,
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确认删除"
                        description="确定要删除这个应用吗？此操作不可恢复。"
                        onConfirm={() => handleDelete(record.id)}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Button
                            type="link"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                        >
                            删除
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Card>
                <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                    <Col>
                        <h2 style={{ margin: 0 }}>应用管理</h2>
                    </Col>
                    <Col>
                        <Space>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={() => fetchApps(currentPage)}
                                loading={loading}
                            >
                                刷新
                            </Button>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={handleAdd}
                            >
                                新增应用
                            </Button>
                        </Space>
                    </Col>
                </Row>

                <Table
                    columns={columns}
                    dataSource={apps}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: total,
                        showSizeChanger: false,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
                        onChange: (page) => fetchApps(page),
                    }}
                    size="middle"
                />
            </Card>

            <Modal
                title={editingApp ? '编辑应用' : '新增应用'}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                okText="确定"
                cancelText="取消"
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    style={{ marginTop: 16 }}
                >
                    <Form.Item
                        label="应用名称"
                        name="app_name"
                        rules={[
                            { required: true, message: '请输入应用名称' },
                            { max: 100, message: '应用名称不能超过100个字符' }
                        ]}
                    >
                        <Input placeholder="请输入应用名称" />
                    </Form.Item>

                    <Form.Item
                        label="应用标签"
                        name="app_tag"
                        rules={[
                            { required: true, message: '请输入应用标签' },
                            { max: 100, message: '应用标签不能超过100个字符' },
                            { pattern: /^[a-zA-Z0-9_-]+$/, message: '应用标签只能包含字母、数字、下划线和连字符' }
                        ]}
                    >
                        <Input placeholder="请输入应用标签，如：pixel_creator" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default AppManagement;